import type { ReactNode } from 'react';
import { KV, KVRow } from '../components/KV';
import type { AppRenderer, AppPayload } from './types';

// Renderer for the `captomi_office` app — the Pete-onboarding profile (the data Pete
// extracts from the call), plus provisioning + billing status. Read-only: edits go through
// the captomi-office service, not the admin. Shape mirrors captomi-office's
// src/data/schemas/app-data.ts (CaptomiOfficeAppData); timestamps arrive as ISO strings.

// ---- shapes (loose — only the fields we display) ----
interface AppData {
  trade?: {
    primary_category?: string;
    additional_categories?: string[];
    vertical_added_at?: string;
  };
  profile?: {
    business_summary?: string;
    website?: string;
    service_areas?: string[];
    specialties?: string[];
    experience?: Record<string, unknown>;
    strengths?: string;
    team_size?: string | null;
    extra_notes?: string[];
    profile_answers?: Record<string, unknown>;
    profile_complete?: boolean;
    profile_complete_at?: string;
    last_updated?: string;
  };
  availability?: {
    // green_zones SHOULD be a GreenZone[]; the captomi-office profile extractor currently
    // writes the raw availability text as a plain string instead — tolerate both shapes
    // so the renderer never crashes on bad upstream data.
    green_zones?: Array<{ day?: string; start?: string; end?: string; repeating?: boolean }> | string;
    blocked_dates?: string[];
  };
  phone_numbers?: {
    receptionist_number?: string | null;
    receptionist_number_status?: string;
    receptionist_number_provisioned_at?: string;
    receptionist_number_twilio_sid?: string;
    receptionist_number_release_started_at?: string;
    receptionist_number_released_at?: string;
    receptionist_number_released_reason?: string;
  };
  billing?: {
    status?: string;
    trial_started_at?: string;
    trial_ends_at?: string;
    monthly_price_usd?: number;
    payment_method_on_file?: boolean;
  };
}

// ---- helpers ----
function fmt_dt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function humanize(token: string): string {
  return token
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

function chips(arr: string[] | undefined, opts?: { humanize?: boolean }): ReactNode {
  if (!arr || arr.length === 0) return <span className="muted">—</span>;
  return (
    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
      {arr.map((v, i) => (
        <span
          key={i}
          style={{
            background: 'var(--green-50)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            padding: '2px 10px',
            fontSize: 13,
          }}
        >
          {opts?.humanize ? humanize(v) : v}
        </span>
      ))}
    </span>
  );
}

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function to12h(hhmm: string | undefined): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm ?? '');
  if (!m) return hhmm ?? '?';
  const h = Number(m[1]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m[2]} ${ampm}`;
}

function green_zone_label(gz: { day?: string; start?: string; end?: string; repeating?: boolean }): string {
  const day = gz.day ? `${humanize(gz.day)}s` : '(day?)';
  return `${day}  ${to12h(gz.start)}–${to12h(gz.end)}${gz.repeating === false ? '' : '  · weekly'}`;
}

function badge(text: string, color: string): ReactNode {
  return <strong style={{ color }}>{text}</strong>;
}

// Onboarding-status derivation: factors in BOTH profile_complete (Pete-onboarding
// signal) AND receptionist_number_status (admin-provisioned signal). Admin-
// provisioned tenants stay at profile_complete=false forever — Pete onboarding
// never runs — but they're operationally ready once the receptionist number is
// ACTIVE. Reading profile_complete alone shows them as perpetually "in progress"
// even though they take real calls.
function onboarding_state(
  profile_complete: boolean | undefined,
  receptionist_status: string | undefined,
): { label: string; color: string } {
  if (profile_complete === true) {
    return { label: 'complete (Pete onboarding)', color: 'var(--green-700)' };
  }
  if (receptionist_status === 'ACTIVE') {
    return { label: 'complete (admin-provisioned)', color: 'var(--green-700)' };
  }
  if (receptionist_status === 'AWAITING_RETELL_BIND') {
    return { label: 'awaiting Retell binding', color: '#b58900' };
  }
  if (receptionist_status === 'PROVISIONING') {
    return { label: 'provisioning…', color: '#b58900' };
  }
  if (receptionist_status === 'RELEASED') {
    return { label: 'number released — re-provision to resume', color: '#b3261e' };
  }
  return { label: 'not started', color: 'var(--muted)' };
}

function receptionist_color(s: string | undefined): string {
  if (s === 'ACTIVE') return 'var(--green-700)';
  if (s === 'AWAITING_RETELL_BIND' || s === 'PROVISIONING') return '#b58900';
  if (s === 'RELEASED') return '#b3261e';
  return 'var(--muted)';
}
function billing_color(s: string | undefined): string {
  if (s === 'ACTIVE') return 'var(--green-700)';
  if (s === 'TRIAL') return '#b58900';
  if (s === 'LAPSED' || s === 'CANCELLED') return '#b3261e';
  return 'var(--muted)';
}

function record_value(v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return <span className="muted">—</span>;
  if (typeof v === 'string') return <span style={{ whiteSpace: 'pre-wrap' }}>{v}</span>;
  if (typeof v === 'number' || typeof v === 'boolean') return <code>{String(v)}</code>;
  if (Array.isArray(v)) return chips(v.map((x) => String(x)));
  return <code>{JSON.stringify(v)}</code>;
}

function record_rows(rec: Record<string, unknown> | undefined): ReactNode {
  const entries = Object.entries(rec ?? {});
  if (entries.length === 0) return <p className="muted" style={{ margin: 0 }}>(none captured)</p>;
  return (
    <KV>
      {entries.map(([k, v]) => (
        <KVRow key={k} label={humanize(k)} value={record_value(v)} />
      ))}
    </KV>
  );
}

// ---- renderer ----
export const CaptomiOfficeRenderer: AppRenderer = {
  app_name: 'captomi_office',
  render(payload: AppPayload) {
    const d = payload.data as AppData;
    const profile = d.profile ?? {};
    const avail = d.availability ?? {};
    const phone = d.phone_numbers ?? {};
    const billing = d.billing ?? {};
    const trade = d.trade ?? {};

    // green_zones should be a GreenZone[], but bad upstream data can make it a plain
    // string — handle both so the renderer can't crash (the bare `.sort()` threw on a
    // string: "(...).slice(...).sort is not a function").
    const green_zones_raw = avail.green_zones;
    const green_zones = Array.isArray(green_zones_raw)
      ? green_zones_raw
          .slice()
          .sort(
            (a, b) =>
              DAY_ORDER.indexOf(a.day ?? '') - DAY_ORDER.indexOf(b.day ?? '') ||
              (a.start ?? '').localeCompare(b.start ?? ''),
          )
      : [];
    const green_zones_text = typeof green_zones_raw === 'string' ? green_zones_raw.trim() : '';

    const answer_count = Object.keys(profile.profile_answers ?? {}).length;

    return (
      <div>
        <h3 style={{ marginTop: 0 }}>Status</h3>
        <KV>
          <KVRow
            label="Onboarding"
            value={(() => {
              const ob = onboarding_state(profile.profile_complete, phone.receptionist_number_status);
              return (
                <>
                  {badge(ob.label, ob.color)}
                  {profile.profile_complete && profile.profile_complete_at && (
                    <>
                      {' '}
                      <span className="muted" style={{ fontSize: 13 }}>· {fmt_dt(profile.profile_complete_at)}</span>
                    </>
                  )}
                </>
              );
            })()}
          />
          <KVRow
            label="Peter number"
            value={
              <>
                <code>{phone.receptionist_number ?? '—'}</code>{' '}
                {badge(phone.receptionist_number_status ?? 'NOT_PROVISIONED', receptionist_color(phone.receptionist_number_status))}
                {phone.receptionist_number_provisioned_at && (
                  <span className="muted" style={{ fontSize: 13 }}> · {fmt_dt(phone.receptionist_number_provisioned_at)}</span>
                )}
                {/* The lease is REMOVED when a release finishes, so its presence alone means a
                    teardown is in flight or stopped partway — the only place that state is visible.
                    Do NOT also gate on `!released_at`: a tenant released once, re-provisioned and
                    now mid-release again carries both fields, and that is precisely the tenant whose
                    warning matters most. */}
                {phone.receptionist_number_release_started_at && (
                  <div style={{ color: '#b3261e', fontSize: 13, marginTop: 2 }}>
                    ⚠ release in flight since {fmt_dt(phone.receptionist_number_release_started_at)} — if
                    this is stale, the teardown stopped partway and the line may not be answering
                  </div>
                )}
                {phone.receptionist_number_released_at && (
                  <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
                    released {fmt_dt(phone.receptionist_number_released_at)}
                    {phone.receptionist_number_released_reason
                      ? ` — ${phone.receptionist_number_released_reason}`
                      : ''}
                  </div>
                )}
              </>
            }
          />
          <KVRow
            label="Billing"
            value={
              <>
                {badge(billing.status ?? 'NOT_STARTED', billing_color(billing.status))}{' '}
                <span className="muted" style={{ fontSize: 13 }}>
                  · ${billing.monthly_price_usd ?? 49}/mo · payment method {billing.payment_method_on_file ? 'on file' : 'not on file'}
                </span>
                {billing.trial_ends_at && (
                  <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                    Trial: {fmt_dt(billing.trial_started_at)} → {fmt_dt(billing.trial_ends_at)}
                  </div>
                )}
              </>
            }
          />
        </KV>

        <h3 style={{ marginTop: 24 }}>Profile</h3>
        <KV>
          {profile.business_summary ? (
            <KVRow label="Summary" value={<span style={{ whiteSpace: 'pre-wrap' }}>{profile.business_summary}</span>} />
          ) : null}
          {profile.website ? (
            <KVRow
              label="Website"
              value={
                <a href={profile.website} target="_blank" rel="noopener noreferrer">
                  {profile.website}
                </a>
              }
            />
          ) : null}
          <KVRow label="Service areas" value={chips(profile.service_areas)} />
          <KVRow label="Specialties" value={chips(profile.specialties, { humanize: true })} />
          {profile.strengths ? (
            <KVRow label="Strengths" value={<span style={{ whiteSpace: 'pre-wrap' }}>{profile.strengths}</span>} />
          ) : null}
          {profile.team_size != null && profile.team_size !== '' ? (
            <KVRow label="Team" value={profile.team_size} />
          ) : null}
          {(profile.extra_notes?.length ?? 0) > 0 ? (
            <KVRow
              label="Other notes"
              value={
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {(profile.extra_notes ?? []).map((n, i) => (
                    <li key={i} style={{ whiteSpace: 'pre-wrap' }}>{n}</li>
                  ))}
                </ul>
              }
            />
          ) : null}
          <KVRow
            label="Trade"
            value={
              <>
                {humanize(trade.primary_category ?? '—')}
                {(trade.additional_categories?.length ?? 0) > 0
                  ? ` (+ ${(trade.additional_categories ?? []).map(humanize).join(', ')})`
                  : ''}
              </>
            }
          />
          <KVRow label="Last updated" value={fmt_dt(profile.last_updated)} />
        </KV>

        <h4 style={{ margin: '20px 0 8px' }}>Experience</h4>
        {record_rows(profile.experience)}

        <h3 style={{ marginTop: 24 }}>Availability for estimates</h3>
        {green_zones.length > 0 ? (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {green_zones.map((gz, i) => (
              <li key={i} style={{ fontSize: 14 }}>{green_zone_label(gz)}</li>
            ))}
          </ul>
        ) : green_zones_text ? (
          // Extractor wrote the raw text instead of structured zones — show it as-is.
          <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{green_zones_text}</p>
        ) : (
          <p className="muted" style={{ margin: 0 }}>(no windows captured)</p>
        )}
        {(avail.blocked_dates?.length ?? 0) > 0 ? (
          <p className="muted" style={{ fontSize: 13, marginTop: 8 }}>Blocked: {(avail.blocked_dates ?? []).join(', ')}</p>
        ) : null}

        {answer_count > 0 ? (
          <details
            style={{ marginTop: 24, border: '1px solid var(--line)', borderRadius: 6, padding: '8px 12px' }}
          >
            <summary style={{ cursor: 'pointer', fontSize: 13 }}>Raw answers ({answer_count})</summary>
            <div style={{ marginTop: 12 }}>{record_rows(profile.profile_answers)}</div>
          </details>
        ) : null}
      </div>
    );
  },
};
